/**
 * Formatea un importe en pesos dominicanos para mostrarlo al usuario.
 *
 * Fuente única del frontend. Antes convivían seis formas distintas de escribir
 * el mismo número —`RD$10691.00`, `RD$ 10,691.00`, `DOP 10,691.00`,
 * `RD$ 10,691`—, de modo que el mismo importe cambiaba de aspecto según la
 * pantalla. Coincide con el `formatMoney` del backend, así que un importe
 * mostrado en la interfaz y el mismo importe dentro de un correo o una
 * notificación se leen igual.
 *
 * @param amount   Importe. Acepta texto porque la API entrega los `decimal`
 *                 como cadena ("990.00").
 * @param symbol   Símbolo a anteponer. Pasa `''` cuando la plantilla ya lo pone.
 */
export function formatDop(
  amount: number | string | null | undefined,
  symbol = 'RD$',
): string {
  const numeric = typeof amount === 'string' ? Number(amount) : (amount ?? 0);
  const safe = Number.isFinite(numeric) ? numeric : 0;

  const formatted = new Intl.NumberFormat('es-DO', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(safe);

  return symbol ? `${symbol}${formatted}` : formatted;
}

/**
 * El mismo importe, sin los centavos cuando no los tiene.
 *
 * Se usa donde el número es el TITULAR y no un renglón de una cuenta: el precio
 * de una tarjeta de plan, el ahorro anual, el precio de entrada de una línea.
 * Ahí «RD$2,100.00» obliga a leer cuatro caracteres que siempre son cero, y en
 * una cifra de 42 px ese ruido se nota.
 *
 * No es un formato distinto: cuando el importe SÍ tiene centavos los muestra,
 * con las mismas reglas de separador y de moneda que `formatDop`. Un precio
 * partido no se puede redondear en pantalla —el cliente lo compararía con el
 * cargo real de su tarjeta— así que el redondeo no existe: sólo se omite lo que
 * no aporta.
 */
export function formatDopHeadline(
  amount: number | string | null | undefined,
  symbol = 'RD$',
): string {
  const numeric = typeof amount === 'string' ? Number(amount) : (amount ?? 0);
  const safe = Number.isFinite(numeric) ? numeric : 0;

  if (!Number.isInteger(safe)) return formatDop(safe, symbol);

  const formatted = new Intl.NumberFormat('es-DO', {
    maximumFractionDigits: 0,
  }).format(safe);

  return symbol ? `${symbol}${formatted}` : formatted;
}
